import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/ops/AppShell';
import {
  Users2, Package, Clock, TrendingUp, AlertTriangle, Activity, ArrowRight,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

function StatTile({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3.5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 leading-tight">{label}</p>
          <p className="text-[22px] font-semibold text-slate-900 leading-tight mt-1.5">{value}</p>
          {sub && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{sub}</p>}
        </div>
        {Icon && (
          <div className="w-7 h-7 rounded-md bg-slate-50 border border-slate-200 grid place-items-center shrink-0">
            <Icon size={13} className="text-slate-500" />
          </div>
        )}
      </div>
    </div>
  );
}

function PanelCard({ title, subtitle, action, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid={testid}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <h3 className="text-[13px] font-semibold text-slate-900 leading-tight">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className="px-1.5 py-1.5">{children}</div>
    </div>
  );
}

const fmtRel = (s) => {
  if (!s) return '—';
  const diff = Date.now() - new Date(s).getTime();
  const h = 36e5, d = 24 * h;
  if (diff < h) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`;
  if (diff < d) return `${Math.floor(diff / h)}h ago`;
  if (diff < 30 * d) return `${Math.floor(diff / d)}d ago`;
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function OpsAdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/ops/login'); return; }
    if (!user.roles?.includes('admin')) { navigate('/ops/login'); return; }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ops/dashboards/admin`, getAuth());
      setData(data);
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user?.roles?.includes('admin')) fetchData(); }, [user, fetchData]);

  if (authLoading || loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const t = data.tiles;
  const deltaLabel = t.active_leads.delta_vs_last_week === null ? '—'
    : t.active_leads.delta_vs_last_week > 0 ? `+${t.active_leads.delta_vs_last_week} vs last week`
    : t.active_leads.delta_vs_last_week < 0 ? `${t.active_leads.delta_vs_last_week} vs last week`
    : 'No change vs last week';

  return (
    <AppShell pageTitle="Dashboard" subtitle="Operations insights" activeNav="dashboard" onNavigate={() => {}} role="admin">
      <div className="px-6 py-5 space-y-4" data-testid="admin-dashboard-page">

        {/* Stat tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-stat-tiles">
          <StatTile testid="tile-active-leads"        label="Active Leads"          value={t.active_leads.count}            sub={deltaLabel} icon={Users2} />
          <StatTile testid="tile-jobs-in-production"  label="Jobs in Production"    value={t.jobs_in_production.count}     icon={Package} />
          <StatTile testid="tile-active-interviewers" label="Active Interviewers"   value={t.active_interviewers.count}   sub={`Last ${t.active_interviewers.window_days} days`} icon={Activity} />
          <StatTile testid="tile-avg-days"            label="Avg Days to Complete"  value={t.avg_days_to_complete.value === null ? '—' : t.avg_days_to_complete.value} sub={`Rolling ${t.avg_days_to_complete.window_days} days`} icon={Clock} />
        </div>

        {/* Funnel + Monthly */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <PanelCard
              title="Pipeline funnel"
              subtitle="Lead counts at each stage"
              testid="pipeline-funnel-card"
            >
              <div style={{ width: '100%', height: 240 }} className="px-2 py-2">
                <ResponsiveContainer>
                  <BarChart data={data.pipeline_funnel} margin={{ top: 10, right: 8, left: -8, bottom: 32 }}>
                    <CartesianGrid stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="stage" tick={{ fontSize: 10, fill: '#64748B' }} interval={0} angle={-22} textAnchor="end" height={50} stroke="#CBD5E1" />
                    <YAxis tick={{ fontSize: 10, fill: '#64748B' }} stroke="#CBD5E1" />
                    <Tooltip cursor={{ fill: '#F1F5F9' }} contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #E2E8F0' }} />
                    <Bar dataKey="count" fill="#0F172A" radius={[2, 2, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </PanelCard>
          </div>
          <PanelCard
            title="Monthly throughput"
            subtitle="Jobs completed, last 6 months"
            testid="monthly-throughput-card"
          >
            <div style={{ width: '100%', height: 240 }} className="px-2 py-2">
              <ResponsiveContainer>
                <LineChart data={data.monthly_throughput} margin={{ top: 10, right: 12, left: -10, bottom: 8 }}>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748B' }} stroke="#CBD5E1" />
                  <YAxis tick={{ fontSize: 10, fill: '#64748B' }} stroke="#CBD5E1" allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #E2E8F0' }} />
                  <Line type="monotone" dataKey="completed" stroke="#0F172A" strokeWidth={1.5} dot={{ r: 2.5, fill: '#0F172A' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </PanelCard>
        </div>

        {/* Aging jobs + bottlenecks */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <PanelCard
              title="Jobs over 30 days old"
              subtitle={`${data.aging_jobs.length} jobs flagged`}
              testid="aging-jobs-card"
              action={
                <button
                  onClick={() => navigate('/ops/production/jobs')}
                  className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                  data-testid="aging-jobs-view-all"
                >
                  View production <ArrowRight size={11} />
                </button>
              }
            >
              {data.aging_jobs.length === 0 ? (
                <p className="text-[12px] text-slate-500 px-3 py-6 text-center">No stale jobs. Everything moving.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.aging_jobs.map(j => (
                    <li key={j.id} className="flex items-center justify-between gap-3 px-2.5 py-2 hover:bg-slate-50/60 rounded-md" data-testid={`aging-row-${j.id}`}>
                      <div className="min-w-0">
                        <p className="text-[13px] text-slate-900 truncate leading-tight">{j.client_name} <span className="text-slate-400">— {j.loved_one_first_name}</span></p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{j.status}</p>
                      </div>
                      <span className="ops-pill bg-amber-50 text-amber-700 border-amber-200 px-1.5 shrink-0">
                        {j.age_days}d
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </PanelCard>
          </div>
          <PanelCard
            title="Top bottleneck tasks"
            subtitle="By avg time in progress"
            testid="bottlenecks-card"
          >
            {data.bottlenecks.length === 0 ? (
              <p className="text-[12px] text-slate-500 px-3 py-6 text-center">—</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.bottlenecks.map(b => (
                  <li key={b.name} className="px-2.5 py-2" data-testid={`bottleneck-${b.name}`}>
                    <p className="text-[13px] text-slate-900 leading-tight">{b.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {b.avg_days}d avg · {b.count} task{b.count === 1 ? '' : 's'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </PanelCard>
        </div>

        {/* Recent activity */}
        <PanelCard
          title="Recent activity"
          subtitle="Last 10 system events"
          testid="recent-activity-card"
        >
          {data.recent_activity.length === 0 ? (
            <p className="text-[12px] text-slate-500 px-3 py-6 text-center">No activity yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_activity.map((a, i) => (
                <li key={i} className="px-2.5 py-2 flex items-start gap-2.5" data-testid={`recent-row-${i}`}>
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${a.is_system ? 'bg-slate-300' : 'bg-slate-900'}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-slate-900 leading-tight truncate">{a.text}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{a.author} · {fmtRel(a.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </PanelCard>

      </div>
    </AppShell>
  );
}
